import { useITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpoint } from '@/api/api.generated';
import type {
  ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveDto,
} from '@/api/models';

export interface UseStaffTopFiveDataParams {
  staffId: number | null;
  enabled?: boolean;
}

export interface UseStaffTopFiveDataResult {
  topFives: ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveDto[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Custom hook to fetch current TopFives for a specific staff member.
 * Returns only TopFives from the current period.
 * 
 * Uses the staffId query parameter to bypass cadence filtering.
 * 
 * @param staffId - The staff member ID to fetch TopFives for
 * @param enabled - Whether the query should run (default: true)
 * 
 * @example
 * ```tsx
 * const { topFives, isLoading, error } = useStaffTopFiveData({
 *   staffId: 123,
 *   enabled: !!selectedStaffId,
 * });
 * 
 * // Filter by cadence type
 * const matchingTopFive = topFives.find(t => 
 *   t.cadence === selectedPeriod.cadenceType
 * );
 * ```
 */
export const useStaffTopFiveData = ({
  staffId,
  enabled = true,
}: UseStaffTopFiveDataParams): UseStaffTopFiveDataResult => {
  const { data, isLoading, isError, error, refetch } =
    useITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpoint(
      {
        staffId: staffId ?? undefined,
      },
      {
        query: {
          enabled: enabled && staffId !== null,
          staleTime: 2 * 60 * 1000, // 2 minutes
          select: (response) => {
            // Get TopFives from current period only
            const current = response.data.currentPeriod?.[0]?.topFives ?? [];
            return current;
          },
        },
        axios: {
          withCredentials: true,
        },
      }
    );

  return {
    topFives: (data as any) ?? [],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};
